import { ErrorMessage, Field, Form, Formik } from "formik"
import * as Yup from "yup"
import _filter from "lodash/filter"
import _map from "lodash/map"

import classNames from "@src/utilities/class-names"
import { countriesList, statesList } from "@src/utilities/list-states-countries"
import {
  deltaEightRestrictedStates,
  thcaRestrictedStates,
  thcRestrictedStates,
} from "@src/utilities/state-restrictions"

const yupValidationSchema = Yup.object().shape({
  firstName: Yup.string()
    .min(1, "Too short")
    .max(75, "Too long")
    .required("Required"),
  lastName: Yup.string()
    .min(1, "Too short")
    .max(75, "Too long")
    .required("Required"),
  streetOne: Yup.string()
    .min(2, "Too short")
    .max(75, "Too long")
    .required("Required"),
  city: Yup.string()
    .min(2, "Too short")
    .max(75, "Too long")
    .required("Required"),
  state: Yup.string().length(2, "Invalid").required("Required"),
  zip: Yup.string().min(5, "Invalid").max(12, "Invalid").required("Required"),
})

export interface CheckoutShippingRequest {
  firstName: string
  lastName: string
  streetOne: string
  streetTwo: string
  city: string
  state: string
  zip: string
  country: string
}

interface CheckoutShippingFormProps {
  hasDeltaEight: boolean
  hasThc: boolean
  hasThca: boolean
  initialFirstName: string
  initialLastName: string
  initialStreetOne: string
  initialStreetTwo: string
  initialCity: string
  initialState: string
  initialZip: string
  initialCountry: string
  isProcessing: boolean
  onFormChanged: () => void
  onSubmit: (request: CheckoutShippingRequest) => Promise<void>
  submitEnabled: boolean
}
export default function CheckoutShippingForm({
  hasDeltaEight,
  hasThc,
  hasThca,
  initialFirstName,
  initialLastName,
  initialStreetOne,
  initialStreetTwo,
  initialCity,
  initialState,
  initialZip,
  initialCountry,
  isProcessing,
  onFormChanged,
  onSubmit,
  submitEnabled,
}: CheckoutShippingFormProps) {
  // Show a single banner using the most specific applicable label, matching
  // the priority order of the server-side check (Delta-8 > THCA > THC), so a
  // product tagged both isThca and isThc only produces the THCA message.
  const restrictedProductLabel =
    [
      [hasDeltaEight, "Delta-8"],
      [hasThca, "THCA"],
      [hasThc, "THC"],
    ].find(([hasProduct]) => hasProduct)?.[1] ?? null

  return (
    <Formik
      initialValues={{
        firstName: initialFirstName,
        lastName: initialLastName,
        streetOne: initialStreetOne,
        streetTwo: initialStreetTwo,
        city: initialCity,
        state: initialState,
        zip: initialZip,
        country: initialCountry,
      }}
      validationSchema={yupValidationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        await onSubmit(values)
        setSubmitting(false)
      }}
    >
      {({ errors, touched, handleChange, handleSubmit, isSubmitting }) => (
        <Form
          onChange={(ev) => {
            onFormChanged()
            handleChange(ev)
          }}
          onSubmit={(ev) => {
            ev.preventDefault()
            handleSubmit(ev)
          }}
        >
          <fieldset disabled={isProcessing || isSubmitting}>
            <legend className="block mb-2">
              <h2 className="font-browser font-medium text-lg">
                Shipping Information
              </h2>
              <p className="max-w-md text-sm text-gray-900">
                Sales tax will be calculated when you complete and validate your
                shipping information below.
              </p>
              {restrictedProductLabel && (
                <p
                  className="max-w-md text-xs text-gray-900 mt-2"
                  dangerouslySetInnerHTML={{
                    __html: `Your cart contains ${restrictedProductLabel} products which are only available in limited states. Please check your state&#8217;s laws before ordering.`,
                  }}
                />
              )}
            </legend>
            <div className="mt-1 bg-white rounded-md shadow-sm -space-y-px">
              <div className="flex -space-x-px">
                <div className="w-1/2 flex-1 min-w-0">
                  <label htmlFor="firstName" className="sr-only">
                    First Name
                  </label>
                  <div className="relative">
                    <Field
                      autoComplete="given-name"
                      className={classNames(
                        "p-2 relative block w-full rounded-none rounded-tl-sm bg-transparent focus:z-10 border border-gray-400",
                        errors.firstName && touched.firstName ? "text-red" : ""
                      )}
                      id="firstName"
                      placeholder="First name"
                      type="text"
                      name="firstName"
                    />
                    <ErrorMessage
                      name="firstName"
                      component="span"
                      className="absolute pointer-events-none font-medium text-xs text-red z-20 top-1 right-2"
                    />
                  </div>
                </div>
                <div className="w-1/2 min-w-0">
                  <label htmlFor="lastName" className="sr-only">
                    Last name
                  </label>
                  <div className="relative">
                    <Field
                      autoComplete="family-name"
                      className="p-2 relative block w-full rounded-none rounded-tr-sm bg-transparent focus:z-10 border border-gray-400"
                      id="lastName"
                      name="lastName"
                      placeholder="Last name"
                      type="text"
                    />
                    <ErrorMessage
                      name="lastName"
                      component="span"
                      className="absolute pointer-events-none font-medium text-xs text-red z-20 top-1 right-2"
                    />
                  </div>
                </div>
              </div>
              <div className="flex -space-x-px">
                <div className="w-3/4 flex-1 min-w-0">
                  <label htmlFor="streetOne" className="sr-only">
                    Street address
                  </label>
                  <div className="relative">
                    <Field
                      autoComplete="address-line1"
                      className={classNames(
                        "p-2 relative block w-full rounded-none bg-transparent focus:z-10 border border-gray-400",
                        errors.streetOne && touched.streetOne ? "text-red" : ""
                      )}
                      id="streetOne"
                      placeholder="Street address"
                      type="text"
                      name="streetOne"
                    />
                    <ErrorMessage
                      name="streetOne"
                      component="span"
                      className="absolute pointer-events-none font-medium text-xs text-red z-20 top-1 right-2"
                    />
                  </div>
                </div>
                <div className="w-1/4 min-w-0">
                  <label htmlFor="streetTwo" className="sr-only">
                    Unit #
                  </label>
                  <Field
                    autoComplete="address-line2"
                    className="p-2 relative block w-full rounded-none bg-transparent focus:z-10 border border-gray-400"
                    id="streetTwo"
                    name="streetTwo"
                    placeholder="Unit #"
                    type="text"
                  />
                </div>
              </div>
              <div className="flex -space-x-px">
                <div className="w-1/2 flex-1 min-w-0">
                  <label htmlFor="city" className="sr-only">
                    City
                  </label>
                  <div className="relative">
                    <Field
                      autoComplete="address-level2"
                      className="p-2 relative block w-full rounded-none bg-transparent focus:z-10 border border-gray-400"
                      id="city"
                      name="city"
                      placeholder="City"
                      type="text"
                    />
                    <ErrorMessage
                      name="city"
                      component="span"
                      className="absolute pointer-events-none font-medium text-xs text-red z-20 top-1 right-2"
                    />
                  </div>
                </div>
                <div className="w-1/4 min-w-0">
                  <label htmlFor="state" className="sr-only">
                    State
                  </label>
                  <div className="relative">
                    <Field
                      as="select"
                      autoComplete="address-level1"
                      className="p-2 relative block w-full rounded-none bg-transparent focus:z-10 border border-gray-400 border-b-0"
                      id="state"
                      name="state"
                    >
                      <option value="">- State</option>
                      {_map(
                        _filter(statesList, (st) =>
                          hasDeltaEight
                            ? !deltaEightRestrictedStates.includes(st.abbreviation)
                            : true
                        ).filter((st) =>
                          hasThca
                            ? !thcaRestrictedStates.includes(st.abbreviation)
                            : true
                        ).filter((st) =>
                          hasThc
                            ? !thcRestrictedStates.includes(st.abbreviation)
                            : true),
                        (state) => (
                          <option
                            dangerouslySetInnerHTML={{ __html: state.name }}
                            key={state.abbreviation}
                            value={state.abbreviation}
                          />
                        )
                      )}
                    </Field>
                    <ErrorMessage
                      name="state"
                      component="span"
                      className="absolute pointer-events-none font-medium text-xs text-red z-20 top-1 right-2"
                    />
                  </div>
                </div>
                <div className="w-1/4 min-w-0">
                  <label htmlFor="zip" className="sr-only">
                    ZIP Code
                  </label>
                  <div className="relative">
                    <Field
                      autoComplete="postal-code"
                      className="p-2 relative block w-full rounded-none bg-transparent focus:z-10 border border-gray-400"
                      id="zip"
                      name="zip"
                      placeholder="ZIP Code"
                      type="text"
                    />
                    <ErrorMessage
                      name="zip"
                      component="span"
                      className="absolute pointer-events-none font-medium text-xs text-red z-20 top-1 right-2"
                    />
                  </div>
                </div>
              </div>
              <div>
                <label htmlFor="country" className="sr-only">
                  Country
                </label>
                <Field
                  as="select"
                  autoComplete="country"
                  className="p-2 relative block w-full rounded-none rounded-bl-sm rounded-br-sm bg-transparent focus:z-10 border border-gray-400"
                  id="country"
                  name="country"
                >
                  {_map(countriesList, (country) => (
                    <option
                      dangerouslySetInnerHTML={{ __html: country.name }}
                      key={country.abbreviation}
                      value={country.abbreviation}
                    />
                  ))}
                </Field>
              </div>
            </div>
            {submitEnabled && (
              <div className="mt-4 text-right">
                <button
                  className="bg-orange inline-block px-12 py-2 font-medium rounded-sm text-sm text-white uppercase hover:bg-orange-600 transition-colors disabled:bg-black disabled:opacity-10"
                  disabled={isSubmitting || !submitEnabled}
                  type="submit"
                >
                  Validate Address
                </button>
              </div>
            )}
          </fieldset>
        </Form>
      )}
    </Formik>
  )
}
